import type { GuideCase } from './panel/prototype-guide';
import type { StateOf, StateOptions } from './panel/prototype-url-state';

/**
 * The panel's states. Each key is a URL parameter with one radio group in index.html (one radio per value),
 * and the first value is the design. Every one maps to something the live page or its script really does.
 */
export const STATE_OPTIONS = {
    /** `.canvas-3d-scene`: the render, the first-load CircularProgress, or no scene configured. */
    scene: ['ready', 'loading', 'none'],
    /** `main[expand]`, toggled by `.sidebar-expand-bt`. */
    sidebar: ['open', 'collapsed'],
    /** Kiosk + ViewOnlyMode: `view-only-mode="1"`, sidebar gone. */
    viewOnly: ['off', 'on'],
    /** The configuration panel, or the summary slide-over (`.canvas-summary-content[expand="1"]`). */
    panel: ['configure', 'summary'],
    /** One product hides the picker step (`.step-pick-item.step-hidden`). */
    products: ['several', 'single'],
    /** Every `.canvas-3d-step` open (as rendered) or closed. */
    steps: ['open', 'closed'],
    text: ['design', 'long'],
    /** Sidebar subtitle and amounts: from price, configured total, or the external price lookup failed (TK). */
    price: ['from', 'configured', 'unknown'],
    /** `.canvas-3d-configurator-bt-add-to-cart` and `.canvas-3d-configurator-add-to-cart-error`. */
    cart: ['ready', 'loading', 'failed', 'canceled', 'disabled'],
    /** `ThreeDConfigRequestQuotationBtn` company variable. */
    quotation: ['hidden', 'shown'],
    /** De Eekhoorn (B2B): purchase + recommended retail price columns instead of the WOOOD total. */
    brand: ['woood', 'de-eekhoorn'],
    /** The AI slider below the configurator, and with it `.canvas-3d-bt-view-room`: live shows both only while the active single has one. */
    aiSlider: ['shown', 'hidden'],
    /** How many renders the AI slider has: live shows one per angle the bucket returns for the configuration. */
    aiImages: ['3', '1', '2', '4', '5'],
    /** The shop language the AI disclaimer is translated into (one `DefaultTranslations.Custom` key per language on live). */
    language: ['nl', 'de', 'fr'],
    /** Components/UspBar: an ordinary component in the page's component loop. */
    uspBar: ['shown', 'hidden'],
    /** The `overview` section: intro and accordions, one column of either (`single-column`), or not placed. */
    overview: ['both', 'intro', 'accordions', 'none'],
} as const satisfies StateOptions;

export type PrototypeState = StateOf<typeof STATE_OPTIONS>;

/** Max content: every edge case at once, so they can be checked together. Keys left out keep the design value. */
export const MAX_CONTENT: Partial<PrototypeState> = {
    text: 'long',
    price: 'configured',
    cart: 'failed',
    quotation: 'shown',
    brand: 'de-eekhoorn',
};

const DESKTOP_HEIGHT = 820;

/** The developer guide (guide.html), top to bottom. */
export const GUIDE_CASES: ReadonlyArray<GuideCase<PrototypeState>> = [
    {
        id: 'design',
        title: 'Design',
        note: 'The first single product is selected and configuring (data-state="configuring"); its edition wrapper is the only one shown. The subtitle shows the from price until the shopper changes something.',
        state: {},
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'max-content',
        title: 'Max content',
        note: 'Long titles, the request-quotation button, the add-to-cart error and the De Eekhoorn price columns together: the bottom bar at its tallest.',
        state: MAX_CONTENT,
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'summary',
        title: 'Summary',
        note: 'A second .sidebar-detail sliding over the panel. Its rows are built by renderSummary() from the active items, only for steps with data-show-in-orderflow="true"; values under a "dimension" edition get a "cm" unit, under a "color" edition the option image.',
        state: { panel: 'summary' },
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'single-product',
        title: 'Single product',
        note: 'One product on the canvas hides the picker with .step-hidden, but the item still renders: the script resolves the configurable product from a .canvas-config-item.',
        state: { products: 'single' },
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'sidebar-collapsed',
        title: 'Sidebar collapsed',
        state: { sidebar: 'collapsed' },
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'scene-loading',
        title: '3D scene loading',
        note: 'The CircularProgress overlay only gets `active` once the script has found a scene iframe; the last few percent are a timer in OneCore3D, not the model.',
        state: { scene: 'loading' },
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'no-scene',
        title: 'No scene configured',
        note: 'Canvas3DScene renders its "noscenetext" (or the NoSceneConfigured translation) and no iframe, so the progress overlay never starts.',
        state: { scene: 'none' },
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'price-unknown',
        title: 'Price unknown',
        note: 'A null product price is unknown, not free: amount slots show the CanvasConfiguratorPriceUnknown label (TK), and the subtitle falls back to the from price.',
        state: { price: 'unknown' },
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'cart-loading',
        title: 'Add to cart: waiting for Power Automate',
        note: 'data-loading="1" swaps the icon and label for the spinner and the panel goes inert; the poll can run for minutes.',
        state: { cart: 'loading' },
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'cart-failed',
        title: 'Add to cart failed',
        note: 'data-error picks one of two server-rendered messages; the redirect handshake fails quietly, so this is the only thing the shopper sees.',
        state: { cart: 'failed' },
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'de-eekhoorn',
        title: 'De Eekhoorn (B2B)',
        note: 'CompanyVariable.IsDeEekhoorn(): purchase price and recommended retail price columns; the retail column hides as a whole when its amount is unknown.',
        state: { brand: 'de-eekhoorn', quotation: 'shown' },
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'view-only',
        title: 'Kiosk view-only',
        note: '?kiosk=1 with the ViewOnlyMode company variable: the sidebar and its toggle are gone and the scene takes the full width.',
        state: { viewOnly: 'on' },
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'ai-slider',
        title: 'AI slider, USP bar and overview',
        note: 'Below the configurator. The AI slider ships hidden and its Swiper is created on first show (a Swiper built on a hidden element measures 0); "Bekijk in je kamer" scrolls to it. The USP bar swipes on tablet and phone with CSS scroll-snap only. The overview is CMS copy: a "Veelgestelde vragen" title and six FAQ accordions.',
        state: {},
        height: 2200,
    },
    {
        id: 'ai-slider-one-image',
        title: 'AI slider: one render',
        note: 'The bucket can return a single angle: the slider keeps its width per slide and Swiper disables both arrows.',
        state: { aiImages: '1' },
        height: 2200,
    },
    {
        id: 'ai-disclaimer-phone',
        title: 'AI disclaimer: phone',
        note: 'The full line on each render, clear of the expand button; at 375 wide it wraps inside the image. The fullscreen popup shows the same line at the bottom middle of the image.',
        state: {},
        width: 375,
        height: 2200,
    },
    {
        id: 'ai-disclaimer-phone-fr',
        title: 'AI disclaimer: French on phone',
        note: 'The French line is the longest of the three translations, so it wraps the most on a narrow render.',
        state: { language: 'fr' },
        width: 375,
        height: 2200,
    },
    {
        id: 'overview-single-column',
        title: 'Overview: accordions only',
        note: 'ConfigurationOverview adds single-column when the intro or the accordions are missing.',
        state: { aiSlider: 'hidden', uspBar: 'hidden', overview: 'accordions' },
        height: 1500,
    },
    {
        id: 'no-sections',
        title: 'Nothing below the configurator',
        note: 'No single product with an AISlider step, no UspBar placed and no overview section: the page ends with the configurator.',
        state: { aiSlider: 'hidden', uspBar: 'hidden', overview: 'none' },
        height: 1200,
    },
    {
        id: 'tablet',
        title: 'Tablet',
        state: {},
        width: 1024,
        height: DESKTOP_HEIGHT,
    },
    {
        id: 'phone',
        title: 'Phone',
        state: MAX_CONTENT,
        width: 375,
        height: DESKTOP_HEIGHT,
    },
];
